/**
 * Coarse duration labels for reply targets.
 *
 * Two rules, and the second one is the whole reason this is a shared file rather than a local
 * helper in whichever component needed it first.
 *
 * **Roll up once minutes stop being readable.** "455m overdue" is 7.6 hours; nobody converts
 * that at a glance in a list they are scanning. Past an hour it becomes hours, past a day it
 * becomes days, and it stays coarse — an SLA breached for half a week does not need its hours.
 *
 * **Always round down, never to nearest.** This is the correction: both call sites used
 * `Math.round`, so 90 minutes remaining rendered as "2h left". An agent triaging a queue reads
 * that as "I have two hours" and picks something else up, when in fact the target passes in
 * ninety minutes. The error is always in the dangerous direction — rounding to nearest
 * overstates the remaining time for every value in the back half of each unit. Flooring can
 * only ever understate it, so the pill is a promise the clock will keep. Overdue labels floor
 * for the same reason they do in every "3h ago" timestamp: "1h overdue" means at least an hour,
 * and reading it as exactly an hour costs nothing.
 *
 * Values below the first threshold are already exact, so nothing is lost there: `59` is "59m",
 * and only `60` and above becomes "1h".
 */
export function humaniseMinutes(mins: number): string {
  const m = Math.max(0, Math.floor(mins))
  if (m < 60) return `${m}m`
  if (m < 60 * 24) return `${Math.floor(m / 60)}h`
  return `${Math.floor(m / (60 * 24))}d`
}

/** Minutes remaining against a reply target, negative once it has passed. */
export function minutesUntil(dueAt: string | null, now: number = Date.now()): number | null {
  if (!dueAt) return null
  const at = new Date(dueAt).getTime()
  if (Number.isNaN(at)) return null
  // Truncated toward zero rather than rounded, so the sign is decided by the real instant:
  // 30 seconds past the target is already overdue, not "0m left".
  return Math.trunc((at - now) / 60_000)
}

/**
 * The full pill text for a reply target — "Answered", "No target set", "20m overdue", "3h left".
 * Shared so the thread header and the properties panel cannot drift apart on wording or on the
 * rounding rule above, which is exactly what happened when each had its own copy.
 */
export function replyTargetLabel(
  input: { firstResponseAt?: string | null; dueAt: string | null },
  now: number = Date.now(),
): string {
  if (input.firstResponseAt) return 'Answered'
  const mins = minutesUntil(input.dueAt, now)
  if (mins === null) return 'No target set'
  return mins < 0 ? `${humaniseMinutes(Math.abs(mins))} overdue` : `${humaniseMinutes(mins)} left`
}
